import { Miniature, Dashboard, HeaderStyle, DashboardConfiguration } from '../../models';
import { IWidgetFactory, IWidget, IWidgetConfiguration } from '../../widgets';
import { Helper, whiteBackgroundColor } from '../../common/';
import { TagsTrendConfiguration } from '../../models/widgets/TagsTrendConfiguration/tagsTrendConfiguration';
import { getDefaultPrintOptions } from '../../states/scaledPrintPreview/getDefaultPrintOptions';

export interface IDashboardFactory {
    createDashboard(type: string): any;
    getMiniatureList(): Miniature[];
}

export class DashboardFactory implements IDashboardFactory {
    static serviceId = 'dashboardFactory';

    dashboardTypes: Miniature[];
    private _fullHeight = 36;

    static $inject = [
        'widgetFactory'
    ];

    constructor(
        private _widgetFactory: IWidgetFactory
    ) {
    }

    createDashboard(type: string): any {
        const dashboard = {
            guid: Helper.getGuid(),
            name: type,
            configuration: {
                printConfiguration: {
                    backgroundColor: whiteBackgroundColor,
                    headerStyle: HeaderStyle.None,
                    isHidingBorder: false,
                    isUseNewPrintSystem: true,
                    printOptions: getDefaultPrintOptions()
                },
                widgets: this.getWidgetsForType(type)
            } as DashboardConfiguration,
            categories: [],
            groupTypes: [],
            backgroundUrl: this.getBackgroundUrlForType(type)
        } as Dashboard;
        return dashboard;
    }

    private getWidgetsForType(type: string): IWidget[] {
        let widgets: IWidget[];
        switch (type) {
            case 'tagsTrend':
                widgets = this.createTagsTrend();
                break;
            case 'inputQuality':
                widgets = this.createInputQualityDashboard();
                break;
            case 'groupInputQuality':
                widgets = this.createGroupInputQuality();
                break;
            case 'flat':
                widgets = this.createFlat();
                break;
            case 'scatter':
                widgets = this.createScatter();
                break;
            case 'outOfRange':
                widgets = this.createOutOfRange();
                break;
            case 'badValues':
                widgets = this.createBadValues();
                break;
            case 'invalidKpis':
                widgets = this.createInvalidKpis();
                break;
            case 'suspiciousKpis':
                widgets = this.createSuspiciousKpis();
                break;
            case 'gauges':
                widgets = this.createGaugeDashboard();
                break;
            case 'missing':
                widgets = this.createMissing();
                break;
            default:
                widgets = [];
        }

        return widgets;
    }

    private getBackgroundUrlForType(type: string): string {
        let backgroundUrl = '';
        switch (type) {
            case 'tagsTrend':
                backgroundUrl = 'Images/Dashboards/tagstrend.png';
                break;
            case 'flat':
                backgroundUrl = 'Images/Dashboards/flattags.png';
                break;
            case 'scatter':
                backgroundUrl = 'Images/Dashboards/scatteredtags.png';
                break;
            case 'outOfRange':
                backgroundUrl = 'Images/Dashboards/outofrangetags.png';
                break;
            case 'badValues':
                backgroundUrl = 'Images/Dashboards/badtags.png';
                break;
            case 'invalidKpis':
                backgroundUrl = 'Images/Dashboards/invalidkpis.png';
                break;
            case 'suspiciousKpis':
                backgroundUrl = 'Images/Dashboards/suspiciouskpis.png';
                break;
            case 'gauges':
                backgroundUrl = 'Images/Dashboards/tagsgauges.png';
                break;
            case 'missing':
                backgroundUrl = 'Images/Dashboards/missingvalues.png';
                break;
        }
        return backgroundUrl;
    }

    private createTagsTrend(): IWidget[] {
        const result = [];
        const config: IWidgetConfiguration = new TagsTrendConfiguration();
        config.sizeX = 12;
        config.sizeY = this._fullHeight;
        const w = this._widgetFactory.createWidget('tagsTrend', config);
        result.push(w);

        return result;
    }

    private createInputQualityDashboard(): IWidget[] {
        const result = [];
        const configPogv: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2 };
        const pogv = this._widgetFactory.createWidget('percentageOfGoodValuesGauge', configPogv);
        result.push(pogv);
        const configPogve: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6 };
        const pogve = this._widgetFactory.createWidget('percentageOfGoodValuesEvolution', configPogve);
        result.push(pogve);
        const configErDis: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const errorsDistribution = this._widgetFactory.createWidget('errorsDistribution', configErDis);
        result.push(errorsDistribution);
        const configBadActors: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6, row: this._fullHeight / 2 };
        const badActors = this._widgetFactory.createWidget('badActors', configBadActors);
        result.push(badActors);

        return result;
    }

    private createGroupInputQuality(): IWidget[] {
        const result = [];
        const configGroupsTree: IWidgetConfiguration = { sizeX: 4, sizeY: this._fullHeight, col: 0, row: 0 };
        const groupsTree = this._widgetFactory.createWidget('groupsTree', configGroupsTree);
        result.push(groupsTree);
        const configPogv: IWidgetConfiguration = { sizeX: 4, sizeY: this._fullHeight / 2, col: 4, row: 0 };
        const pogv = this._widgetFactory.createWidget('percentageOfGoodValuesGauge', configPogv);
        result.push(pogv);
        const configPogve: IWidgetConfiguration = { sizeX: 4, sizeY: this._fullHeight / 2, col: 8, row: 0 };
        const pogve = this._widgetFactory.createWidget('percentageOfGoodValuesEvolution', configPogve);
        result.push(pogve);
        const configErDis: IWidgetConfiguration = { sizeX: 4, sizeY: this._fullHeight / 2, col: 4, row: this._fullHeight / 2 };
        const errorsDistribution = this._widgetFactory.createWidget('errorsDistribution', configErDis);
        result.push(errorsDistribution);
        const configBadActors: IWidgetConfiguration = { sizeX: 4, sizeY: this._fullHeight / 2, col: 8, row: this._fullHeight / 2 };
        const badActors = this._widgetFactory.createWidget('badActors', configBadActors);
        result.push(badActors);

        return result;
    }

    private createFlat(): IWidget[] {
        const result = [];
        const configPogv: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2 };
        const pogv = this._widgetFactory.createWidget('percentageOfFlatGauge', configPogv);
        result.push(pogv);
        const configPogve: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6 };
        const pogve = this._widgetFactory.createWidget('percentageOfFlatEvolution', configPogve);
        result.push(pogve);
        const configBaf: IWidgetConfiguration = { sizeX: 12, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const baf = this._widgetFactory.createWidget('badActorsFlat', configBaf);
        result.push(baf);

        return result;
    }

    private createScatter(): IWidget[] {
        const result = [];
        const configPogv: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2 };
        const pogv = this._widgetFactory.createWidget('percentageOfScatterGauge', configPogv);
        result.push(pogv);
        const configPogve: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6 };
        const pogve = this._widgetFactory.createWidget('percentageOfScatterEvolution', configPogve);
        result.push(pogve);
        const configBaf: IWidgetConfiguration = { sizeX: 12, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const baf = this._widgetFactory.createWidget('badActorsScatter', configBaf);
        result.push(baf);

        return result;
    }

    private createOutOfRange(): IWidget[] {
        const result = [];
        const configPogv: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2 };
        const pogv = this._widgetFactory.createWidget('percentageOfOutOfRangeGauge', configPogv);
        result.push(pogv);
        const configPogve: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6 };
        const pogve = this._widgetFactory.createWidget('percentageOfOutOfRangeEvolution', configPogve);
        result.push(pogve);
        const configBaf: IWidgetConfiguration = { sizeX: 12, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const baf = this._widgetFactory.createWidget('badActorsOutOfRange', configBaf);
        result.push(baf);

        return result;
    }

    private createBadValues(): IWidget[] {
        const result = [];
        const configGauge: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2 };
        const gauge = this._widgetFactory.createWidget('percentageOfBadValuesGauge', configGauge);
        result.push(gauge);
        const configEvolution: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6 };
        const evolution = this._widgetFactory.createWidget('percentageOfBadValuesEvolution', configEvolution);
        result.push(evolution);
        const configErDis: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const errorsDistribution = this._widgetFactory.createWidget('errorsDistribution', configErDis);
        result.push(errorsDistribution);
        const configBadActors: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6, row: this._fullHeight / 2 };
        const badActors = this._widgetFactory.createWidget('badActors', configBadActors);
        result.push(badActors);

        return result;

    }

    private createGaugeDashboard(): IWidget[] {
        const result = [];
        const configBadValues: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2 };
        const badValues = this._widgetFactory.createWidget('percentageOfBadValuesGauge', configBadValues);
        result.push(badValues);
        const configFlat: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6, row: 0 };
        const flat = this._widgetFactory.createWidget('percentageOfFlatGauge', configFlat);
        result.push(flat);
        const configScatter: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const scatter = this._widgetFactory.createWidget('percentageOfScatterGauge', configScatter);
        result.push(scatter);
        const configOutOfRange: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6, row: this._fullHeight / 2 };
        const outOfRange = this._widgetFactory.createWidget('percentageOfOutOfRangeGauge', configOutOfRange);
        result.push(outOfRange);

        return result;
    }

    private createInvalidKpis(): IWidget[] {
        const result = [];
        const configGauge: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: 0, configuration: { isIdentifierMode: false } };
        const gauge = this._widgetFactory.createWidget('invalidKPIsGauge', configGauge);
        result.push(gauge);
        const configEvolution: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6, row: 0 };
        const evolution = this._widgetFactory.createWidget('percentageOfInvalidKPIsEvolution', configEvolution);
        result.push(evolution);
        const configBadActors: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const badActors = this._widgetFactory.createWidget('badActorsInvalidKpis', configBadActors);
        result.push(badActors);

        return result;
    }

    private createSuspiciousKpis(): IWidget[] {
        const result = [];
        const configGauge: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: 0, configuration: { isIdentifierMode: false } };
        const gauge = this._widgetFactory.createWidget('suspiciousKPIsGauge', configGauge);
        result.push(gauge);
        const configEvolution: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6, row: 0 };
        const evolution = this._widgetFactory.createWidget('percentageOfSuspiciousKPIsEvolution', configEvolution);
        result.push(evolution);
        const configBadActors: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const badActors = this._widgetFactory.createWidget('badActorsSuspiciousKpis', configBadActors);
        result.push(badActors);

        return result;
    }

    private createMissing(): IWidget[] {
        const result = [];
        const configGauge: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: 0, configuration: { isIdentifierMode: false } };
        const gauge = this._widgetFactory.createWidget('missingGauge', configGauge);
        result.push(gauge);
        const configEvolution: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6, row: 0 };
        const evolution = this._widgetFactory.createWidget('percentageOfMissingEvolution', configEvolution);
        result.push(evolution);
        const configBadActorsByConnectionPoint: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 0, row: this._fullHeight / 2 };
        const badActorsByConnectionPoint = this._widgetFactory.createWidget('badActorsByConnectionPointMissing', configBadActorsByConnectionPoint);
        result.push(badActorsByConnectionPoint);
        const configBadActors: IWidgetConfiguration = { sizeX: 6, sizeY: this._fullHeight / 2, col: 6, row: this._fullHeight / 2 };
        const badActors = this._widgetFactory.createWidget('badActorsMissing', configBadActors);
        result.push(badActors);

        return result;
    }

    getMiniatureList(): Miniature[] {
        try {
            if (!this.dashboardTypes) {
                this.dashboardTypes = [];

                const blank = new Miniature('addDashboard', 'Empty dashboard', 'fa-plus',
                    [
                        'A new empty dashboard',
                        'Let your creativity talk'
                    ]);
                this.dashboardTypes.push(blank);

                const gauges = new Miniature('gauges', 'Tag gauges', 'fa-tachometer',
                    [
                        'Bad tags',
                        'Flat tags',
                        'Scattered tags',
                        'Out-of-range tags'
                    ]);
                this.dashboardTypes.push(gauges);

                const missing = new Miniature('missing', 'Missing values', 'fa-step-forward fa-rotate-90',
                    [
                        'Trend of percentage',
                        'Shows which tags have missing values'
                    ]);
                this.dashboardTypes.push(missing);

                const badValues = new Miniature('badValues', 'Bad tags', 'fa-bars',
                    [
                        'Percentage of bad tags',
                        'Show which tags are not ok'
                    ]);
                this.dashboardTypes.push(badValues);

                const flat = new Miniature('flat', 'Flat tags', 'fa-stethoscope',
                    [
                        'Percentage of flat tags',
                        'Show which tags are flat'
                    ]);
                this.dashboardTypes.push(flat);

                const oor = new Miniature('outOfRange', 'Out-of-range tags', 'fa-bars',
                    [
                        'Percentage of out-of-range tags',
                        'Show which tags are out of their bounds'
                    ]);
                this.dashboardTypes.push(oor);

                const scatter = new Miniature('scatter', 'Scattered tags', 'fa-spinner',
                    [
                        'Percentage of scattered tags',
                        'Show which tags are scattered'
                    ]);
                this.dashboardTypes.push(scatter);

                const suspiciousKpis = new Miniature('suspiciousKpis', 'Suspicious KPIs', 'fa-line-chart',
                    [
                        'Suspicious KPIs',
                        'Trend of percentage',
                        'Tags inducing suspicious KPIs'
                    ]);
                this.dashboardTypes.push(suspiciousKpis);

                const invalidKpis = new Miniature('invalidKpis', 'Invalid KPIs', 'fa-line-chart',
                    [
                        'Invalid KPIs',
                        'Trend of percentage',
                        'Tags inducing invalid KPIs'
                    ]);
                this.dashboardTypes.push(invalidKpis);

                const tagsTrend = new Miniature('tagsTrend', 'Tags trend', 'fa-line-chart',
                    [
                        'Trend of tags over time'
                    ]);
                this.dashboardTypes.push(tagsTrend);
            }
            return this.dashboardTypes;

        } catch (e) {
            console.error(e);
        }
        return [];
    }
}